import { useEffect, useState, type RefObject } from "react";

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

const MAX_PARALLAX = 60;
const HEIGHT_EPSILON = 0.5;
const PARALLAX_DAMPEN_START = 0.8;
const PARALLAX_DAMPEN_FACTOR = 0.35;
const SCALE_MAX_BOOST = 0.05;

export function useCollapsibleHeader({
  isActive,
  isMobile,
  contentRef,
  parallaxRef,
  maxHeightPx,
  minHeightPx,
  collapseRangePx,
  initialCollapseProgress = 0,
}: {
  isActive: boolean;
  isMobile: boolean;
  contentRef: RefObject<HTMLElement | null>;
  parallaxRef?: RefObject<HTMLElement | null>;
  maxHeightPx: number;
  minHeightPx: number;
  collapseRangePx: number;
  initialCollapseProgress?: number;
}) {
  const [headerHeight, setHeaderHeight] = useState(maxHeightPx);

  useEffect(() => {
    if (isActive && isMobile) {
      const clamped = clamp(initialCollapseProgress, 0, 1);
      const initialHeight = maxHeightPx - (maxHeightPx - minHeightPx) * clamped;
      setHeaderHeight(initialHeight);
      const el = contentRef.current;
      if (el) {
        const initialScrollTop = collapseRangePx * clamped;
        if (Math.abs(el.scrollTop - initialScrollTop) > 1) {
          el.scrollTop = initialScrollTop;
        }
      }
      const parallaxEl = parallaxRef?.current;
      if (parallaxEl) {
        parallaxEl.style.transform = "translate3d(0, 0px, 0) scale(1)";
      }
    }
  }, [
    isActive,
    isMobile,
    contentRef,
    parallaxRef,
    maxHeightPx,
    minHeightPx,
    collapseRangePx,
    initialCollapseProgress,
  ]);

  useEffect(() => {
    if (!isMobile || !isActive) return;
    const el = contentRef.current;
    if (!el) return;
    const parallaxEl = parallaxRef?.current ?? null;

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        const scrollTop = el.scrollTop;
        const collapseProgress = clamp(scrollTop / collapseRangePx, 0, 1);
        const eased = 1 - Math.pow(1 - collapseProgress, 3);
        const dampen =
          collapseProgress > PARALLAX_DAMPEN_START
            ? 1 - (collapseProgress - PARALLAX_DAMPEN_START) * PARALLAX_DAMPEN_FACTOR
            : 1;
        const parallaxOffset = eased * MAX_PARALLAX * dampen;
        const scale = 1 + (1 - collapseProgress) * SCALE_MAX_BOOST;

        const next = maxHeightPx - (maxHeightPx - minHeightPx) * collapseProgress;
        setHeaderHeight((prev) => (Math.abs(prev - next) > HEIGHT_EPSILON ? next : prev));
        if (parallaxEl) {
          parallaxEl.style.transform = `translate3d(0, ${-parallaxOffset}px, 0) scale(${scale})`;
        }
        rafId = 0;
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      el.removeEventListener("scroll", onScroll);
      if (parallaxEl) {
        parallaxEl.style.transform = "translate3d(0, 0px, 0) scale(1)";
      }
    };
  }, [isMobile, isActive, contentRef, parallaxRef, maxHeightPx, minHeightPx, collapseRangePx]);

  return { headerHeight };
}

